/**
 * Validates if files are ZIP files
 * @param {FileList|File[]} files - Files to validate
 * @returns {File[]} Array of valid ZIP files
 */
export const validateZipFiles = (files) => {
  const fileArray = Array.from(files);
  return fileArray.filter(
    (file) =>
      file.type === 'application/zip' ||
      file.name.toLowerCase().endsWith('.zip')
  );
};

/**
 * Creates file objects with metadata for state management
 * @param {File[]} files - Array of File objects
 * @returns {Object[]} Array of file objects with id, file, name, and size
 */
export const createFileObjects = (files) => {
  return files.map((file) => ({
    id: Date.now() + Math.random(),
    file,
    name: file.name,
    size: file.size,
  }));
};

/**
 * Formats file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

/**
 * Extracts filename from file path and cleans it for display
 * @param {string} filePath - Full file path
 * @param {string} defaultName - Default filename if extraction fails
 * @returns {string} Extracted and cleaned filename
 */
export const extractFileName = (filePath, defaultName = 'Project Template') => {
  if (!filePath) return defaultName;

  const fileName = filePath.split('/').pop() || defaultName;

  // Pattern matches: filename-uuid.extension or filename_uuid.extension
  const uuidPattern =
    /[-_][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

  const cleanedFileName = fileName.replace(uuidPattern, '');

  return cleanedFileName || defaultName;
};

/**
 * Handles file input change with validation
 * @param {Event} event - File input change event
 * @param {Function} setAttachedFiles - State setter function
 * @param {Object} options - Options object
 * @param {string[]} options.allowedTypes - Array of allowed file types/extensions
 * @returns {void}
 */
export const handleFileInputChange = (
  event,
  setAttachedFiles,
  options = { allowedTypes: ['.zip'] }
) => {
  const files = Array.from(event.target.files);
  let validFiles;

  if (options.allowedTypes.includes('.zip')) {
    validFiles = validateZipFiles(files);
  } else {
    validFiles = files;
  }

  if (validFiles.length > 0) {
    const newFiles = createFileObjects(validFiles);
    setAttachedFiles((prev) => [...prev, ...newFiles]);
  }

  event.target.value = '';
};

/**
 * Removes a file from the attached files array
 * @param {string|number} fileId - ID of the file to remove
 * @param {Function} setAttachedFiles - State setter function
 * @returns {void}
 */
export const removeAttachedFile = (fileId, setAttachedFiles) => {
  setAttachedFiles((prev) => prev.filter((file) => file.id !== fileId));
};

export const handleDownloadFileFallback = (downloadUrl) => {
  return new Promise((resolve, reject) => {
    try {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      resolve();
    } catch (fallbackError) {
      reject(fallbackError);
    }
  });
};

/**
 * Downloads a file by creating a temporary download link and programmatically clicking it
 * Falls back to opening the file in a new tab if the download fails
 * @param {string} fileUrl - Base URL for file downloads
 * @param {string} filePath - Relative path to the file
 * @param {string} fileName - Name to use for the downloaded file
 * @returns {Promise<void>} Promise that resolves when download is initiated or rejects on error
 */
export const handleDownloadFile = (fileUrl, filePath, fileName) => {
  return new Promise((resolve, reject) => {
    const downloadUrl = `${fileUrl}${filePath}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    link.rel = 'noopener noreferrer';

    link.addEventListener('error', () => {
      handleDownloadFileFallback(downloadUrl).then(resolve).catch(reject);
    });

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    resolve();
  });
};
